// tokenPreview.ts - v12.10.27 (#2146 Q31 {conversation.channel} known + previewed as "call")
// tokenPreview.ts - v12.10.17 (#1747): {contact.firstName} / {contact.lastName} / {contact.name} preview as the sample contact Maria Lopez.
// tokenPreview.ts - v12.10.12 (#1519): {call.timeGreeting} previews as the greeting for NOW at the practice (Good morning / afternoon / evening).
// tokenPreview.ts - v12.10.4 (#1120): {request.feedbackLink} previews as a sample short link.
// tokenPreview.ts - v12.9.24 (#925): {organization.agentName} is a known token; previews as the sample 'Victoria'.
// tokenPreview.ts - v12.5.3
// v12.5.3 (#543): WIRE_SAMPLE_VALUES + CANONICAL_SAMPLE_BY_TYPE - the canonical twin of SAMPLE_VALUES. The map
// below is SPOKEN text for prompt previews and must stay that way; the integration drawer needs the canonical
// value a live call sends, and reusing the spoken one made every wire format silently inert.
// v12.4.16 (#498): external.* preview samples - the values an integration hands back.
// v12.2.43.2 (#235): subject.*/request.* preview samples via canonSampleKey (was showing [firstName]); +more sample fields
// VoiceSpectrum.Web.T2 - lib/tokenPreview - v12.2.38.0 (#206 sample values + segmented preview; PRE-TOK2 base)
// Shared client-side token resolver for the "PREVIEW" boxes (Greeting, Language-detect fallback,
// Classify, Voicemail, Handoff-busy). Mirrors the CallEngine runtime TokenResolver so what the tenant
// sees in preview matches what the caller hears:
//   - {organization.*} is an alias for {tenant.*} (case-insensitive)
//   - tenant.practiceName / tenant.name  -> practiceName
//   - tenant.phone                       -> phone
//   - tenant.supported_languages         -> supportedLanguages  (e.g. "English and Spanish")
//   - tenant.primary_language            -> primaryLanguage
// A token that CANNOT be known at design time (caller.*, captured slots, node outputs) is shown as a
// readable [lastSegment] marker — never a silent blank — so gaps surface but the preview stays legible.

export type TokenContext = {
  practiceName?: string | null;
  phone?: string | null;
  supportedLanguages?: string | null;
  primaryLanguage?: string | null;
  // v12.3.10 (#297): the rest of Settings -> Organization, mirroring EffectiveConfig so the PREVIEW
  // shows exactly what TokenResolver will speak. Anything missing here renders as a [marker], which is
  // how {organization.timezone} used to look correct in code and fail on a live call.
  timezone?: string | null;
  legalName?: string | null;
  email?: string | null;
  website?: string | null;
  address?: string | null;
  industry?: string | null;
  contactName?: string | null;
  contactRole?: string | null;
  /** v12.10.12 (#1519): the language being edited, for {call.timeGreeting} (default English). */
  lang?: string | null;
};

// ── v12.4.5 (#439): ONE RESOLVER. THERE WERE TWO, AND ONLY ONE KNEW ABOUT SAMPLES. ────────────────
//
// `resolveTokens` (this function) and `resolveTokenSegments` both turned a template into preview text, and
// they disagreed: segments consults SAMPLE_VALUES, this one had a `switch` over tenant.* and bracketed
// EVERYTHING else. PromptEditor uses segments; AiAgentSections uses this - so in the Workflow Designer
// {request.referenceNumber} previewed as a sample, and in AI Agent settings the very same token previewed as
// "[Request > Reference number]". No capture token could EVER preview on that screen.
//
// Verified live on harlemm 2026-09-03, on the 12.4.5 deploy: inserting {request.referenceNumber} into the
// Voicemail greeting rendered "[Request > Reference number]" while the designer showed the sample.
//
// THIS IS TUSHAR'S COMPLAINT IN ITS PUREST FORM - "token is coming here and token is not coming there" - and
// #435/#436/#437/#438 fixed the LISTS and the SAMPLE KEYS while leaving a second resolver that never read
// either. Two implementations of one concept is the disease; deleting one is the cure.
//
// It now delegates, so the two previews cannot drift again. Function declarations hoist, so the forward
// reference is fine.
export function resolveTokens(text: string | undefined | null, ctx: TokenContext): string {
  if (!text) return '';
  return resolveTokenSegments(text, ctx).map(s => s.text).join('');
}

// v12.4.5 (#440): the keys `resolveKnown` below actually resolves, exported so the DESIGNER'S "Unrecognized
// token" warning can agree with them. It was driven by the served vocabulary catalog alone, which does not
// carry organization.practiceName - so the Greeting card warned "Unrecognized token:
// {organization.practiceName} - dropped at call time" while the preview two lines below it rendered
// "Harlem Medical". A validator that contradicts the thing it validates is worse than no validator.
export const RESOLVER_KNOWN_TOKENS: string[] = [
  'practiceName', 'name', 'phone', 'supported_languages', 'primary_language', 'timezone',
  'legalName', 'email', 'website', 'address', 'industry', 'contactName', 'contactRole',
  'agentName',   // v12.9.24 (#925)
].flatMap(f => [`organization.${f}`, `tenant.${f}`]).concat(['call.timeGreeting', 'conversation.channel']);   // v12.10.12 (#1519) · v12.10.27 (#2146 Q31)

// v12.4.5 (#439): the second resolver that used to live here is DELETED, not kept "just in case".
// It is the reason the two previews disagreed for months; leaving it would invite the next person to
// call it. resolveTokens above delegates to resolveTokenSegments - one implementation, one behaviour.

function nonBlank(v: string | null | undefined): string | null {
  return v && v.trim() ? v : null;
}

// v12.2.38.0 (#206): design-time SAMPLE values for capture tokens that only resolve on a live call. Shown in
// the PREVIEW box with distinct styling + a footnote so the sentence reads naturally while editing, WITHOUT
// pretending they are real captured values. Keys are compared lower-case.
export const SAMPLE_VALUES: Record<string, string> = {
  // v12.9.24 (#925): per-language at call time (the voice agent speaking), so the design-time preview is a SAMPLE.
  'tenant.agentname': 'Victoria',
  'agent.name': 'Victoria',
  'patient.name': 'John Doe',
  'patient.firstname': 'John',
  'patient.lastname': 'Doe',
  'patient.dob': 'March 4, 1985',
  'patient.zip': '07083',
  'patient.phone': '(555) 123-4567',
  'patient.memberid': 'MBR-100245',
  'patient.address1': '12 Main St',
  'patient.address2': 'Apt 4',
  'patient.city': 'Newark',
  'patient.state': 'NJ',
  'patient.county': 'Essex',
  'patient.country': 'USA',
  'appointment.date': 'Tuesday, Aug 5, 2026',
  'appointment.time': '1:00 PM',
  // v12.4.5 (#438): KEYS MUST BE LOWERCASE. resolveTokenSegments does `key.toLowerCase()` before the lookup,
  // so any key here containing a capital can NEVER match and its token previews as a [bracketed marker]
  // instead of a sample. Four keys were dead this way - newDate, newTime and both referenceNumbers - which is
  // the unfound half of #405 ("why request.referenenumber is not visible in anything else prompt?"): that fix
  // wired the editors' token LISTS and nobody checked that the PREVIEW for those tokens resolved.
  'appointment.newdate': 'Thursday, Aug 14, 2026',   // v12.2.47.10 (#278): Change-flow new-appointment samples
  'appointment.newtime': '3:15 PM',
  'appointment.reason': 'a check-up',
  // v12.3.14 (#371): the call's reference. Grouped, no '#': that prefix is display-only in the Messages
  // drawer, and this token may be SPOKEN by the agent.
  // v12.4.5 (#435): the caller-facing shape after BDR-006 - last six digits, grouped. It was still showing
  // the retired ten-digit YYMMDD form, so every prompt author previewed a format the engine stopped issuing.
  'appointment.referencenumber': '36-0025',
  'request.referencenumber': '36-0025',
  // v12.10.4 (#1120): the Text card's {request.feedbackLink} previewed as a sample short link (call 36-25), not a
  // [Request › Feedback link] marker - the marker also named the group differently from the token in the box ("Visit").
  'appointment.feedbacklink': 'api.voicespectrum.ai/f/harlemm/36-25',
  'request.feedbacklink': 'api.voicespectrum.ai/f/harlemm/36-25',
  'appointment.place': 'the Main St office',
  'appointment.provider': 'Dr. Lee',
  'caller.name': 'John Doe',
  'caller.firstname': 'John',
  'caller.lastname': 'Doe',
  'caller.phone': '(555) 123-4567',
  // v12.10.17 (#1747): the contact the ID check asks about - "Am I speaking with Maria?" (UI-PROTO-ID-CHECK-T123 v1.1).
  'contact.firstname': 'Maria',
  'contact.lastname': 'Lopez',
  'contact.name': 'Maria Lopez',
  // v12.4.16 (#498): the external.* family. These fill from an action's RESPONSE mapping, so at design time
  // they are as unknowable as a captured value and get samples for the same reason - a token the picker
  // offers must never preview as a [bracketed marker]. The samples deliberately look like another system's
  // identifiers rather than ours: {external.reference} is the practice's confirmation number, and
  // {request.referenceNumber} (36-0025 above) is ours. Two different numbers, two different shapes.
  'external.id': 'A-4471',
  'external.reference': 'CONF-88213',
  'external.message': 'Appointment created',
  'external.status': 'true',
};

// v12.5.3 (#543): THE WIRE TWIN OF SAMPLE_VALUES, and the reason the two must never be the same map.
//
// SAMPLE_VALUES above exists so a PROMPT reads naturally while it is being written: "your appointment on
// Tuesday, Aug 5, 2026". That is spoken text and it is correct for that job. It is NOT what goes on the wire.
// A captured date is stored canonically - FieldValues.CanonicalDate, 'yyyy-MM-dd' - and FieldValues.Wire()
// parses that canonical form EXACTLY before applying a tenant's format. Hand it "Tuesday, Aug 5, 2026" and
// the parse fails, the value falls through unformatted, and whatever format the tenant picked is silently
// ignored.
//
// #496 reused SAMPLE_VALUES for the integration drawer's Run test, so the drawer posted spoken dates while a
// live call posted canonical ones. Tushar hit it on 2026-09-20: he tokenised visitDate, chose
// 'yyyy-MM-dd HH:mm:ss.fff', and PM Web answered "Visit Date is required." - because we had sent it
// "Tuesday, Aug 5, 2026" with the format never applied. The format layer was fine. The sample was lying.
//
// So: dates and times get a canonical twin here, and only here. Every other token (names, ZIPs, phone,
// identifiers) is already wire-shaped in SAMPLE_VALUES and deliberately has no entry below.
// ── v12.6.1 (#546): A SAMPLE WITH A DATE IN IT HAS A SHELF LIFE. ───────────────────────────────────
//
// MY DEFECT, SHIPPED IN 12.5.3. #543 replaced the drawer's SPOKEN samples with CANONICAL ones so a chosen
// wire format would actually apply - correct, and it works. But I mirrored the spoken samples literally, and
// they were ALREADY IN THE PAST: `appointment.date` was hard-coded `2026-08-05`. So from the day it shipped,
// every Run test on a booking action asked the other system to book a date that had gone. Tushar hit it on
// his first test the next morning and we spent an hour on PM Web 400s before he bisected it himself.
//
// THE TWO KINDS OF DATE ARE NOT THE SAME KIND OF SAMPLE, and that is the whole fix:
//   an APPOINTMENT date must be in the FUTURE, or a booking API is entitled to refuse it;
//   a DATE OF BIRTH must be in the PAST, or it is not a date of birth.
// One is computed from today, the other is fixed. Getting that backwards is what #549 refuses on a live call.
//
// Computed at MODULE LOAD, which is the right granularity: the drawer is re-opened far more often than the
// tab is reloaded, and a sample that changed under the tenant mid-session would make two runs of "the same"
// test disagree. Re-running the same test next month cannot start failing because the sample aged.
function isoDaysFromToday(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A future date for anything that books. Far enough out that a practice's lead time cannot reject it. */
const SAMPLE_FUTURE_DATE = isoDaysFromToday(14);
const SAMPLE_FUTURE_DATE_2 = isoDaysFromToday(23);

export const WIRE_SAMPLE_VALUES: Record<string, string> = {
  'patient.dob': '1985-03-04',                // FIXED, and deliberately: a date of birth is in the past
  'appointment.date': SAMPLE_FUTURE_DATE,     // computed - today + 14
  'appointment.time': '13:00',                // a time of day has no shelf life
  'appointment.newdate': SAMPLE_FUTURE_DATE_2,// computed - today + 23, so a reschedule moves FORWARD
  'appointment.newtime': '15:15',
};

// The fallback when a token is typed date/time but has no named twin above - a tenant custom field, say.
// Matches FieldValues.CanonicalDate / CanonicalTime exactly; if those ever change, these change with them.
//
// v12.6.1 (#546): the untyped fallback is FUTURE too. A custom date field the drawer knows nothing about is
// far more likely to be something being booked than something being remembered, and a future sample fails
// safe: an API that wanted a past date will say so, where a past one silently books nothing.
export const CANONICAL_SAMPLE_BY_TYPE: Record<string, string> = {
  date: SAMPLE_FUTURE_DATE,
  time: '13:00',
};

// v12.2.43.2 (#235): the token list is canonical (subject.*), but the samples above are keyed patient.* /
// appointment.* (the aliases). Normalize a lookup key so {subject.firstName} and {request.date} find the same
// sample as {patient.firstName} / {appointment.date}. Mirrors the engine's runtime alias (patient<->subject,
// appointment<->request).
function canonSampleKey(k: string): string {
  if (k.startsWith('subject.')) return 'patient.' + k.slice('subject.'.length);
  if (k.startsWith('request.')) return 'appointment.' + k.slice('request.'.length);
  return k;
}

// ── v12.10.12 (#1519 T66): {call.timeGreeting} - the same rule as the engine (TokenResolver.TimeOfDay): the practice's
// clock, morning until 11:59 AM, afternoon 12:00-4:59 PM, evening from 5 PM. The preview shows the value for NOW.
export const TIME_GREETING_WORDS: Record<string, [string, string, string]> = {
  en: ['Good morning', 'Good afternoon', 'Good evening'],
  es: ['Buenos d\u00edas', 'Buenas tardes', 'Buenas noches'],
};
export function timeOfDayIndex(timezone?: string | null, now: Date = new Date()): 0 | 1 | 2 {
  let hour = now.getUTCHours();
  try {
    const h = new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone: timezone && timezone.trim() ? timezone.trim() : 'UTC' }).format(now);
    const n = parseInt(h, 10);
    if (!Number.isNaN(n)) hour = n % 24;
  } catch { /* an unknown zone - UTC, as the engine does */ }
  return hour < 12 ? 0 : hour < 17 ? 1 : 2;
}
export function timeGreeting(timezone?: string | null, lang?: string | null, now: Date = new Date()): string {
  const l = (lang ?? 'en').slice(0, 2).toLowerCase();
  return (TIME_GREETING_WORDS[l] ?? TIME_GREETING_WORDS.en)[timeOfDayIndex(timezone, now)];
}

export type PreviewSegment = { text: string; kind: 'literal' | 'value' | 'sample' | 'marker' };

function resolveKnown(k: string, ctx: TokenContext): string | null {
  switch (k) {
    case 'tenant.practicename':
    case 'tenant.name': return nonBlank(ctx.practiceName);
    case 'tenant.phone': return nonBlank(ctx.phone);
    case 'tenant.supported_languages': return nonBlank(ctx.supportedLanguages);
    case 'tenant.primary_language': return nonBlank(ctx.primaryLanguage);
    case 'tenant.timezone': return nonBlank(ctx.timezone);
    case 'tenant.legalname': return nonBlank(ctx.legalName);
    case 'tenant.email': return nonBlank(ctx.email);
    case 'tenant.website': return nonBlank(ctx.website);
    case 'tenant.address': return nonBlank(ctx.address);
    case 'tenant.industry': return nonBlank(ctx.industry);
    case 'tenant.contactname': return nonBlank(ctx.contactName);
    case 'tenant.contactrole': return nonBlank(ctx.contactRole);
    case 'call.timegreeting': return timeGreeting(ctx.timezone, ctx.lang);   // v12.10.12 (#1519)
    // v12.10.27 (#2146 Q31): the preview reads it as a call ("call"); a text conversation says "text".
    case 'conversation.channel': { const l = (ctx.lang || 'en').slice(0, 2).toLowerCase(); return l === 'es' ? 'llamada' : l === 'zh' ? '来电' : 'call'; }
    default: return null;
  }
}

// v12.2.38.0 (#206): like resolveTokens, but returns typed segments so the preview can render tenant values
// plainly, capture-token SAMPLES with distinct styling, and truly-unresolvable tokens as [marker].
export function resolveTokenSegments(text: string | undefined | null, ctx: TokenContext): PreviewSegment[] {
  if (!text) return [];
  const segs: PreviewSegment[] = [];
  const re = /\{([^}]+)\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) segs.push({ text: text.slice(last, m.index), kind: 'literal' });
    let key = String(m[1]).trim();
    if (/^organization\./i.test(key)) key = 'tenant.' + key.slice('organization.'.length);
    const k = key.toLowerCase();
    const resolved = resolveKnown(k, ctx);
    if (resolved !== null) segs.push({ text: resolved, kind: 'value' });
    else if (SAMPLE_VALUES[canonSampleKey(k)] !== undefined) segs.push({ text: SAMPLE_VALUES[canonSampleKey(k)], kind: 'sample' });
    else segs.push({ text: bracket(key), kind: 'marker' });
    last = re.lastIndex;
  }
  if (last < text.length) segs.push({ text: text.slice(last), kind: 'literal' });
  return segs;
}

// Readable placeholder for a token that can't be resolved at design time (e.g. caller.phone -> [phone]).
// v12.3.11 (#311): a token that cannot be resolved at design time shows its ENTITY and FIELD, not just the
// last segment. {medication.name} previewed as "[name]", which reads like a bug and tells the author nothing
// about which entity the value comes from — especially when several entities have a "name" field.
function prettySeg(seg: string): string {
  const spaced = seg
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')   // daysOfSupply -> days Of Supply
    .trim();
  if (!spaced) return seg;
  const lower = spaced.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);   // -> "Days of supply"
}

function bracket(key: string): string {
  const d = key.indexOf('.');
  if (d < 0) return `[${prettySeg(key)}]`;
  return `[${prettySeg(key.slice(0, d))} \u203a ${prettySeg(key.slice(d + 1))}]`;
}
